import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";

const BASE_URL = "https://nissi-india.com"; // change if needed

function useQuery() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}

const months = [
  { text: "Select", value: "Select" },
  { text: "January", value: "1" },
  { text: "February", value: "2" },
  { text: "March", value: "3" },
  { text: "April", value: "4" },
  { text: "May", value: "5" },
  { text: "June", value: "6" },
  { text: "July", value: "7" },
  { text: "August", value: "8" },
  { text: "September", value: "9" },
  { text: "October", value: "10" },
  { text: "November", value: "11" },
  { text: "December", value: "12" },
];

function fmt2(v) {
  const n = Number((v ?? "").toString().replace(/,/g, "").trim());
  if (!Number.isFinite(n)) return "0.00";
  return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function upper(v) {
  return (v ?? "").toString().toUpperCase();
}

const SalaryAbstractReportPrint = () => {
  const q = useQuery();

  // query params from listing page
  const finYearId = q.get("finYearId") || localStorage.getItem("finYearId") || "";
  const branchType = q.get("branchType") || localStorage.getItem("branchtype") || "";
  const loginBranchId = q.get("loginBranchId") || localStorage.getItem("branchid") || "";
  const branchId = q.get("branchId") || "Select";
  const monthName = q.get("monthName") || "Select";
  const year = q.get("year") || "Select";
  const isPF = q.get("isPF") || ""; // yes/no/Honorarium
  const payMode = q.get("payMode") || ""; // Cash/Bank (not shown in screenshot)

  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState({
    grossPay: 0,
    pf: 0,
    advance: 0,
    loan: 0,
    esi: 0,
    tds: 0,
    netpay: 0,
  });

  const [branchName, setBranchName] = useState("");

  // Company info (optional)
  const companyName = localStorage.getItem("companyName") || "Nissi Infotech";
  const companyLine1 = localStorage.getItem("companyLine1") || "Nissi";
  const companyLine2 = localStorage.getItem("companyLine2") || "CIVIL CONSTRUCTION";
  const companyAddr =
    localStorage.getItem("companyAddr") ||
    "3/42-12, Nagappa colony, kavundampalayam - 641030 , Tamil Nadu, India.";
  const companyPhone = localStorage.getItem("companyPhone") || "Phone No :9003744644";
  const companyEmail = localStorage.getItem("companyEmail") || "infraconsoft-implementation@nissi";

  const titleText = useMemo(() => {
    // Screenshot: "SALARY ABSTRACT -HONORARIUMFOR THE MONTH OF FEBRUARY- 2026 - HEAD-OFFICE"
    // We'll format: SALARY ABSTRACT - {isPF} FOR THE MONTH OF {MONTH}- {YEAR} - {BRANCH}
    const pfPart = isPF ? `-${upper(isPF)}` : "";
    const branchPart = branchName ? ` - ${upper(branchName)}` : "";
    return `SALARY ABSTRACT ${pfPart} FOR THE MONTH OF ${upper(monthName)}- ${year}${branchPart}`;
  }, [isPF, monthName, year, branchName]);

  const fetchBranchName = async () => {
    if (!String(finYearId).trim()) return;
    if (!String(loginBranchId).trim() || !String(branchType).trim()) return;

    try {
      const res = await axios.get(`${BASE_URL}/api/salary-abstract-report/meta`, {
        params: {
          finYearId: Number(finYearId),
          branchId: Number(loginBranchId),
          branchType: String(branchType),
        },
      });

      if (res?.data?.success) {
        const list = res.data.branches || [];
        const idToFind = branchId && branchId !== "Select" ? Number(branchId) : Number(loginBranchId);
        const b = list.find((x) => Number(x.fld_branchid) === Number(idToFind));
        setBranchName(b?.fld_branchname || "");
      }
    } catch {
      // ignore
    }
  };

  const fetchPrintData = async () => {
    if (!String(finYearId).trim()) return;

    try {
      setLoading(true);

      const res = await axios.get(`${BASE_URL}/api/salary-abstract-report/print-data`, {
        params: {
          finYearId: Number(finYearId),
          branchType: String(branchType),
          loginBranchId: Number(loginBranchId),
          branchId,
          monthName,
          year,
          isPF,
          payMode,
        },
      });

      if (res?.data?.success) {
        setRows(res.data.rows || []);
        setTotals(
          res.data.totals || {
            grossPay: 0,
            pf: 0,
            advance: 0,
            loan: 0,
            esi: 0,
            tds: 0,
            netpay: 0,
          }
        );
      } else {
        setRows([]);
      }
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBranchName();
    fetchPrintData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePrint = () => window.print();

  return (
    <div className="print-root">
      <style>{`
        .print-root{
          background:#fff;
          color:#000;
          padding: 16px;
          font-family: "Times New Roman", Times, serif;
        }
        .page {
          border: 2px solid #000;
          padding: 14px;
          max-width: 1100px;
          margin: 0 auto;
        }
        .headerBox{
          border:1px solid #000;
          padding: 10px;
          display:flex;
          gap:14px;
          align-items:center;
          justify-content:space-between;
        }
        .logoBox{
          width:140px;
          height:75px;
          border:1px solid #000;
          display:flex;
          align-items:center;
          justify-content:center;
          font-weight:700;
          font-size:12px;
        }
        .companyText{
          flex:1;
          text-align:center;
          line-height:1.15;
        }
        .companyText .name{ font-weight:700; font-size:20px; }
        .companyText .sub{ font-weight:700; font-size:13px; }
        .companyText .addr{ font-size:12px; margin-top:2px; }

        .titleWrap{
          margin-top: 12px;
          border: 1px solid #000;
          border-left: none;
          border-right: none;
          padding: 10px 0;
          text-align:center;
          font-weight:700;
          font-size:16px;
        }

        table{
          width:100%;
          border-collapse:collapse;
          margin-top: 14px;
          font-size:13px;
        }
        th, td{
          border:1px solid #000;
          padding:8px 8px;
          vertical-align:middle;
        }
        th{
          font-weight:700;
          text-align:center;
        }
        td{ text-align:center; }
        .left{ text-align:left; }
        .right{ text-align:right; }
        .totRow td{ font-weight:700; }

        .footerSign{
          margin-top: 26px;
          border-top:1px solid #000;
          padding-top: 12px;
          display:flex;
          justify-content:space-between;
          font-weight:700;
          font-size:14px;
        }

        .actions{
          max-width:1100px;
          margin: 12px auto 0;
          display:flex;
          justify-content:flex-end;
          gap:10px;
        }
        .btn{
          border:1px solid #000;
          background:#fff;
          padding:8px 14px;
          cursor:pointer;
          font-weight:700;
        }

        @media print{
          .actions{ display:none; }
          .print-root{ padding:0; }
          .page{ border:none; padding:0; max-width:unset; margin:0; }
          @page { margin: 10mm; }
        }
      `}</style>

      {/* Print button */}
      <div className="actions">
        <button className="btn" onClick={handlePrint}>Print</button>
      </div>

      <div className="page">
        {/* Header */}
        <div className="headerBox">
          <div className="logoBox">LOGO</div>

          <div className="companyText">
            <div className="name">{companyName}</div>
            <div className="sub">{companyLine1}</div>
            <div className="sub">{companyLine2}</div>
            <div className="addr">{companyAddr}</div>
            <div className="addr">{companyPhone}</div>
            <div className="addr">{companyEmail}</div>
          </div>

          <div style={{ width: 140 }} />
        </div>

        {/* Title */}
        <div className="titleWrap">{titleText}</div>

        {/* Table */}
        <table>
          <thead>
            <tr>
              <th style={{ width: "7%" }}>S.No</th>
              <th style={{ width: "23%" }}>Department</th>
              <th style={{ width: "12%" }}>Gross Salary</th>
              <th style={{ width: "8%" }}>PF</th>
              <th style={{ width: "10%" }}>Others</th>
              <th style={{ width: "10%" }}>Advance</th>
              <th style={{ width: "8%" }}>ESI</th>
              <th style={{ width: "8%" }}>TDS</th>
              <th style={{ width: "14%" }}>Net Pay</th>
            </tr>
          </thead>

          <tbody>
            {loading ? (
              <tr>
                <td colSpan={9} className="left">Loading...</td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={9}>No Records Found</td>
              </tr>
            ) : (
              rows.map((r, idx) => (
                <tr key={idx}>
                  <td>{idx + 1}</td>
                  <td className="left">{r.fld_departmentname || ""}</td>
                  <td className="right">{fmt2(r.grossPay)}</td>
                  <td className="right">{fmt2(r.pf)}</td>
                  <td className="right">{fmt2(r.advance)}</td>
                  <td className="right">{fmt2(r.loan)}</td>
                  <td className="right">{fmt2(r.esi)}</td>
                  <td className="right">{fmt2(r.tds)}</td>
                  <td className="right">{fmt2(r.netpay)}</td>
                </tr>
              ))
            )}

            {/* Total row */}
            {!loading && rows.length > 0 && (
              <tr className="totRow">
                <td />
                <td>Total</td>
                <td className="right">{fmt2(totals.grossPay)}</td>
                <td className="right">{fmt2(totals.pf)}</td>
                <td className="right">{fmt2(totals.advance)}</td>
                <td className="right">{fmt2(totals.loan)}</td>
                <td className="right">{fmt2(totals.esi)}</td>
                <td className="right">{fmt2(totals.tds)}</td>
                <td className="right">{fmt2(totals.netpay)}</td>
              </tr>
            )}
          </tbody>
        </table>

        {/* Footer signatures */}
        <div className="footerSign">
          <div>Prepared By</div>
          <div>Checked By</div>
          <div>Sr.Manager Accounts</div>
        </div>
      </div>
    </div>
  );
};

export default SalaryAbstractReportPrint;
